import { setGA4Event } from "@granitegroup/Handlers/Utilities/CustomTracking";
import { AddDefaultWHParameter, setDefaultWarehouse } from "@granitegroup/Services/CommonServices";
import loadProductCategories from "@granitegroup/Store/Handlers/Custom/LoadProductCategories";
import loadDefaultWarehouse from "@granitegroup/Store/Handlers/Custom/LoadWarehouse";
import { CustomState } from "@granitegroup/Store/Reducers";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler, HandlerProps, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = AddDefaultWHParameter & HasOnSuccess<WarehouseModel[]>;

type Props = {
    apiResult: boolean;
    apiParameter: AddDefaultWHParameter;
};

type HandlerType = Handler<Parameter, Props>;

const dispatchIsLoading = (isLoading: boolean, props: HandlerProps<Parameter, Props>) => {
    props.dispatch({
        type: "Custom/DefaultWarehouseState/Loading",
        isLoading,
    } as any);
};

export const SetLoadingTrue: HandlerType = props => {
    dispatchIsLoading(true, props);
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        const result = await setDefaultWarehouse(props.parameter);
        if (result.successful) {
            props.apiResult = result.result;
            props.dispatch(loadDefaultWarehouse({}));
        } else {
            throw new Error(result.errorMessage);
        }
    } catch (error) {
        if ("status" in error && (error.status === 400 || error.status === 404)) {
            props.dispatch({
                type: "Custom/DefaultWarehouseState/FailedToLoadDefaultWarehouse",
                countries: [],
                message: "Failed to update default warehouse",
            } as any);
            return false;
        }
        throw error;
    }
};

export const DispatchGAEvent: HandlerType = async props => {
    // Wait for the warehouse update to be reflected in state
    await waitFor(() => {
        const state = props.getState() as CustomState;
        return state.customDefaultWarehouse.warehouse?.id === props.parameter.id;
    }, 1000); // Wait for 1000ms

    const state = props.getState() as CustomState;
    const isCartPage = getCurrentPage(state).type === "CartPage";
    if (!isCartPage) {return;}

    const session = state.context.session;

    const { cartId } = getQueryStrings(state);

    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    const cartLines = cartState.value?.cartLines;
    const currency = session?.currency?.currencyCode || "";
    const billToCustomerName = getBillToState(state, cartState.value?.billToId)?.value?.customerNumber ?? "";
    const orderSubTotal = cartState.value?.orderSubTotal || 0;
    const affiliation = state.customDefaultWarehouse.warehouse?.description || "";
    let dispatchGA4Event = false;

    if (cartLines && cartLines.length > 0) {
        props.dispatch(
            loadProductCategories({
                productIds: cartLines.map(o => o.productId).filter((id): id is string => id !== null),
                type: "cart",
                onComplete: data => {
                    dispatchGA4Event = true;
                    const productCategories = data.apiResult;
                    setGA4Event(cartLines, "view_cart", {
                        currency,
                        value: orderSubTotal,
                        affiliation,
                        account_number: billToCustomerName,
                        productCategories,
                    });
                },
            }),
        );
    }
    await waitFor(() => dispatchGA4Event);
};

export const SetLoadingFalse: HandlerType = props => {
    dispatchIsLoading(false, props);
};

export const chain = [SetLoadingTrue, RequestDataFromApi, DispatchGAEvent, SetLoadingFalse];

const updateDefaultWarehouse = createHandlerChainRunner(chain, "UpdateDefaultWarehouse");
export default updateDefaultWarehouse;
